import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { User } from '../types/auth';
import { userService } from '../lib/userService'; // Replace tokenService with userService
import { useMoodboard } from './MoodboardContext';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isUpdatingTokens: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  checkTokenBalance: (user: User | null) => Promise<void>;
  refreshTokenBalance: (user: User | null) => Promise<void>;
  updateUserTokens: (newTokenBalance: number) => void;
  updateUser: (updatedUser: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isUpdatingTokens, setIsUpdatingTokens] = useState(false);
  const isUpdatingTokensRef = useRef(false);
  const { preloadMoodboardData } = useMoodboard(); // Get the preload function

  const checkTokenBalance = useCallback(async (user: User | null) => {
    if (user?.id && !isUpdatingTokensRef.current) {
      try {
        isUpdatingTokensRef.current = true;
        setIsUpdatingTokens(true);
        // Use the new unified service for a single database call
        const completeUserData = await userService.getCompleteUserData(user.id, user.email);
        setUser(completeUserData);
      } catch (error) {
        console.error('Error checking user data:', error);
      } finally {
        isUpdatingTokensRef.current = false;
        setIsUpdatingTokens(false);
      }
    }
  }, []);

  // Add a new function for manual token refresh that bypasses cache
  const refreshTokenBalance = useCallback(async (user: User | null) => {
    if (user?.id && !isUpdatingTokensRef.current) {
      try {
        isUpdatingTokensRef.current = true;
        setIsUpdatingTokens(true);
        // Clear cache before fetching to force fresh data
        userService.clearUserCache(user.id);
        const completeUserData = await userService.getCompleteUserData(user.id, user.email);
        setUser(completeUserData);
      } catch (error) {
        console.error('Error refreshing user data:', error);
      } finally {
        isUpdatingTokensRef.current = false;
        setIsUpdatingTokens(false);
      }
    }
  }, []);

  const updateUserTokens = (newTokenBalance: number) => {
    setUser(prev => {
      if (prev) {
        // Update both local state and cache
        userService.updateUserTokens(prev.id, newTokenBalance);
        return { ...prev, tokens: newTokenBalance };
      }
      return null;
    });
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  useEffect(() => {
    console.log('🚀 AuthContext initializing...');
    setLoading(true);

    // Handle OAuth callback tokens in URL hash
    const handleAuthCallback = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('access_token')) {
        console.log('🔗 Processing OAuth callback tokens...');
        try {
          // Let Supabase handle the session from URL
          const { error } = await supabase.auth.getSession();
          if (error) {
            console.error('Error processing OAuth callback:', error);
          } else {
            console.log('✅ OAuth callback processed successfully');
          }
          // Clear the hash from URL
          window.history.replaceState(null, '', window.location.pathname);
        } catch (error) {
          console.error('Error handling OAuth callback:', error);
        }
      }
    };

    handleAuthCallback();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Only log significant auth state changes
        if (event !== 'INITIAL_SESSION') {
          console.log('🔄 Auth state change:', event, session ? session.user.id : 'no session');
        }

        if (session?.user) {
          // Set user immediately with basic info to unblock UI
          const basicUser: User = {
            id: session.user.id,
            email: session.user.email || '',
            tokens: 0, // Will be updated separately
            is_admin: false,
            user_metadata: {
              full_name: session.user.user_metadata?.full_name || '',
              avatar_url: session.user.user_metadata?.avatar_url || ''
            }
          };
          setUser(basicUser);
          setLoading(false); // Unblock UI immediately

          // Fetch complete user data in the background (non-blocking)
          setTimeout(async () => {
            try {
              console.log('🔄 Fetching complete user data in background...');
              const completeUserData = await userService.getCompleteUserData(session.user.id, session.user.email);
              setUser(completeUserData);
              // Preload moodboard data after complete user data is loaded
              preloadMoodboardData();
            } catch (error) {
              console.error("❌ Error fetching complete user data in background", error);
              // Keep the basic user data if background fetch fails
            }
          }, 100); // Small delay to allow UI to render first
        } else {
          // No session, user is logged out.
          setUser(null);
          setLoading(false);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [preloadMoodboardData]); // Include preloadMoodboardData in dependencies

  const signUp = async (email: string, password: string) => {
    console.log('📝 Signing up user:', email);
    
    try {
      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password 
      });
      
      if (error) {
        console.error('❌ Supabase signup error:', error);
        
        // Handle specific error types
        if (error.message.includes('Database error saving new user')) {
          throw new Error('Database setup issue. Please check that the database schema is properly configured. Contact support if this persists.');
        } else if (error.message.includes('User already registered')) {
          throw new Error('An account with this email already exists. Try signing in instead.');
        } else if (error.message.includes('Signup is disabled')) {
          throw new Error('Account registration is currently disabled. Please contact support.');
        } else {
          throw error;
        }
      }
      
      if (data.user) {
        console.log('✅ Signup successful for:', email, 'User ID:', data.user.id);
        console.log('📧 Email confirmation required:', !data.user.email_confirmed_at);
      }
    } catch (err: any) {
      console.error('❌ Signup failed:', err);
      throw err;
    }
  };

  const signIn = async (email: string, password: string) => {
    console.log('🔐 Signing in user:', email);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signInWithGoogle = async () => {
    console.log('🔐 Signing in with Google');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    console.log('👋 Signing out user');
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const value = {
    user,
    loading,
    isUpdatingTokens,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    checkTokenBalance,
    refreshTokenBalance,
    updateUserTokens,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 